import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useApi } from "~hooks/apiHooks/useApi";
import { useGetBuildFixtureNameFunc } from "~hooks/useGetBuildFixtureNameFunc.ts";
import type { Broadcast } from "~types/broadcast.ts";
import type { Fixture, FixtureResponse } from "~types/fixture";

type FixtureBroadcastResponse = {
  fixtureName: string;
  broadcasts: Array<Broadcast>;
  startTimeLocal: string | null;
};

export const useGetFixtureBroadcast = (options: Omit<UseQueryOptions<FixtureBroadcastResponse, unknown>, "queryKey" | "queryFn"> = {}) => {
  const request = useApi();
  const { sport, organizationId, fixtureId } = useParams();

  const buildFixtureName = useGetBuildFixtureNameFunc();

  if (!sport || !organizationId || !fixtureId) {
    console.error({ sport, organizationId, fixtureId });
    throw new Error("Missing required params");
  }

  return useQuery<FixtureBroadcastResponse, unknown>({
    queryKey: ["broadcast", sport, organizationId, fixtureId],
    queryFn: () =>
      request<FixtureResponse>(`${sport}/o/${organizationId}/fixtures/${fixtureId}?include=${["persons", "entities"].join(",")}`).then(
        (response) => {
          const fixture: Fixture | undefined = response?.data?.[0];
          const resources = response?.includes?.resources;
          const fixtureName = buildFixtureName(fixture, resources);
          return {
            fixtureName,
            broadcasts: fixture?.broadcasts || [],
            startTimeLocal: fixture?.startTimeLocal,
          };
        },
      ),
    ...options,
  });
};
